export interface FeatureStep {
  icon: string;
  title: string;
  description: string;
}

export interface FeatureBenefit {
  icon: string;
  title: string;
  description: string;
}

export interface FeaturePageData {
  slug: string;
  /** Short name shown in the mega menu */
  navTitle: string;
  /** One-liner shown in the mega menu */
  navDescription: string;
  navIcon: string;
  isNew?: boolean;
  badge: string;
  headline: string;
  subheadline: string;
  steps: FeatureStep[];
  benefits: FeatureBenefit[];
  stats: { value: string; label: string }[];
  faq: { q: string; a: string }[];
  ctaHeadline: string;
  ctaSubheadline: string;
}

export const featurePages: Record<string, FeaturePageData> = {
  'review-firewall': {
    slug: 'review-firewall',
    navTitle: 'Block Bad Reviews',
    navDescription: 'Unhappy customers go to a private form, not Google',
    navIcon: 'shield',
    badge: 'Reputation Protection',
    headline: 'Keep bad reviews off Google. Handle problems in private first.',
    subheadline:
      'One upset customer on a bad night can undo months of great work. Visto catches unhappy customers before they reach Google — giving you a chance to fix the problem and keep your rating clean.',
    steps: [
      {
        icon: 'qr_code_2',
        title: 'Customer scans your QR code',
        description:
          'Place a small QR code on tables, receipts, or the door. One scan starts the review. No app needed, no login required.',
      },
      {
        icon: 'fork_right',
        title: 'Happy customers go to Google. Unhappy ones don\'t.',
        description:
          '4–5 stars? They\'re sent straight to your Google review page. 1–3 stars? They see a private message instead — not Google.',
      },
      {
        icon: 'notifications_active',
        title: 'You get a text alert right away',
        description:
          'The moment someone is unhappy, you get a text message. You can often reach out and fix it before they even leave.',
      },
    ],
    benefits: [
      {
        icon: 'block',
        title: 'Complaints stay between you and the customer',
        description:
          'Negative feedback goes to your private inbox, not the internet. You get to respond before it becomes a public problem.',
      },
      {
        icon: 'bolt',
        title: 'Instant text alerts',
        description:
          'Get a text the second someone is unhappy. Fast action can turn a bad moment into a loyal regular.',
      },
      {
        icon: 'trending_up',
        title: 'Your Google rating goes up over time',
        description:
          'When unhappy reviews are kept private and happy ones go to Google, your public score starts to climb naturally.',
      },
      {
        icon: 'history',
        title: 'All feedback saved in one place',
        description:
          'Every complaint is saved with the date, rating, and contact info. Easy to look back and spot patterns.',
      },
    ],
    stats: [
      { value: '94%', label: 'of complaints intercepted before Google' },
      { value: '3×', label: 'more 5-star reviews generated' },
      { value: '<30s', label: 'to alert you of a bad rating' },
    ],
    faq: [
      {
        q: 'Can an unhappy customer still go post on Google themselves?',
        a: "Yes — Visto can't stop someone from going to Google on their own. But most people won't bother if they already left feedback through your QR code. Our customers see 94% of complaints stay private.",
      },
      {
        q: 'What if a customer closes the form without submitting?',
        a: 'Their low rating is still saved in your dashboard. You\'ll know they were unhappy even if they didn\'t write anything.',
      },
      {
        q: 'Can I choose what score triggers the private form?',
        a: 'Yes. You pick the cutoff — 3 stars, 4 stars, or 5 stars only. You can even set different rules for different QR codes.',
      },
    ],
    ctaHeadline: 'Ready to protect your Google rating?',
    ctaSubheadline: 'Takes 3 minutes to set up. No tech skills needed.',
  },

  'five-star-automation': {
    slug: 'five-star-automation',
    navTitle: '5-Star Automation',
    navDescription: 'Push happy customers straight to Google Maps',
    navIcon: 'star',
    badge: 'Review Growth Engine',
    headline: 'Make it effortless for happy customers to leave you a 5-star review.',
    subheadline:
      "Most happy customers never leave a review — not because they don't want to, but because it's too many steps. Visto makes it a single tap from your QR code straight to Google.",
    steps: [
      {
        icon: 'qr_code_2',
        title: 'Happy customer scans and rates',
        description:
          'After a great experience, the customer scans your QR code and gives you 4 or 5 stars. Takes about 8 seconds.',
      },
      {
        icon: 'open_in_new',
        title: 'They land right on your Google review page',
        description:
          'One tap takes them directly to your Google review form — already open, ready to type. No searching, no extra steps.',
      },
      {
        icon: 'trending_up',
        title: 'More reviews means a higher Google ranking',
        description:
          'The more recent reviews you get, the higher you show up in local Google searches. More visibility brings more new customers.',
      },
    ],
    benefits: [
      {
        icon: 'speed',
        title: 'One tap is all it takes',
        description:
          'Scan → rate → Google. That\'s it. No account needed, no app to download, no hunting for your listing.',
      },
      {
        icon: 'visibility',
        title: 'Show up higher on Google Maps',
        description:
          'Google ranks businesses with more frequent, recent reviews higher in local results. Reviews you collect with Visto count toward this.',
      },
      {
        icon: 'diversity_3',
        title: 'Works anywhere you do business',
        description:
          'Put your QR code on receipts, table cards, invoices, or a sign by the door. Share the link by text or email too.',
      },
      {
        icon: 'schedule',
        title: 'Keeps working while you\'re busy',
        description:
          'Set it up once. Visto runs in the background collecting reviews every day — without any extra work from you.',
      },
    ],
    stats: [
      { value: '+127%', label: 'average increase in monthly reviews' },
      { value: '8s', label: 'average time to complete a review' },
      { value: '4.7★', label: 'average rating after 90 days with Visto' },
    ],
    faq: [
      {
        q: 'Will this work on iPhones and Androids?',
        a: "Yes. Visto works on every phone and every browser. Customers tap the QR code and land straight on Google — no special app or settings required.",
      },
      {
        q: 'Can I collect reviews on Yelp or TripAdvisor too?',
        a: 'Yes. You can set up multiple review destinations and let customers choose, or route them automatically based on their rating.',
      },
      {
        q: "Is this against Google's rules?",
        a: "No. Your customers are real people writing real reviews through Google's own website. Visto just makes it easier for them to get there.",
      },
    ],
    ctaHeadline: 'Start getting more 5-star reviews this week.',
    ctaSubheadline: 'Try it free for 7 days. No credit card needed.',
  },

  'social-media': {
    slug: 'social-media',
    navTitle: 'Social Media Poster',
    navDescription: 'Turn 5-star reviews into branded Instagram content',
    navIcon: 'auto_awesome',
    badge: 'Marketing Hub',
    headline: 'Your customers already wrote your best marketing. We turn it into posts.',
    subheadline:
      "Every 5-star review is a free ad — but most business owners never use them. Visto takes your real Google reviews and turns them into ready-to-post Instagram and Facebook graphics. In minutes, not hours.",
    steps: [
      {
        icon: 'sync',
        title: 'Connect your Google reviews',
        description:
          'Link your Google Business profile and Visto pulls in all your 5-star reviews automatically. It stays updated on its own.',
      },
      {
        icon: 'auto_awesome',
        title: 'We design the post for you',
        description:
          'Pick a review and Visto builds a professional branded graphic with your logo, your colors, and the customer\'s own words. No design skills needed.',
      },
      {
        icon: 'download',
        title: 'Download and post in one click',
        description:
          'Save your branded post as a high-quality image. Post it to Instagram, Facebook, or Stories — wherever your customers are.',
      },
    ],
    benefits: [
      {
        icon: 'brush',
        title: 'Always looks professional',
        description:
          'Upload your logo once and pick your brand color. Every post comes out perfectly on-brand — no Canva, no designer required.',
      },
      {
        icon: 'format_quote',
        title: 'Real customer words that people trust',
        description:
          'People trust other customers far more than ads. Sharing genuine 5-star reviews as social posts gets way more engagement.',
      },
      {
        icon: 'high_quality',
        title: 'Post-ready in minutes',
        description:
          'Most business owners spend hours trying to come up with social media content. With Visto, your best content is always one click away.',
      },
      {
        icon: 'bar_chart',
        title: 'Use it everywhere',
        description:
          'The same image works on Instagram, Facebook, Stories, WhatsApp, your email newsletter, and even printed on your menu or window.',
      },
    ],
    stats: [
      { value: '3 min', label: 'to create a branded social post' },
      { value: '100%', label: 'authentic content from real customers' },
      { value: '8×', label: 'more engagement than stock photography' },
    ],
    faq: [
      {
        q: 'Do I need to connect my Instagram or Facebook account?',
        a: 'No. Visto creates the image and you download it. You post it yourself wherever you want. Auto-scheduling is coming soon.',
      },
      {
        q: 'Can I edit the text before posting?',
        a: "Yes. You can shorten or highlight a specific part of the review before generating the image.",
      },
      {
        q: "What if I don't have a Google Business Profile?",
        a: "You can still use this feature — just paste in any review text manually. Setting up a Google Business Profile is free and takes about 10 minutes.",
      },
    ],
    ctaHeadline: 'Turn your best reviews into content that works for you.',
    ctaSubheadline: 'Your customers already wrote it. Visto does the rest.',
  },

  'employee-qr': {
    slug: 'employee-qr',
    navTitle: 'Employee QR Codes',
    navDescription: 'Know which staff member is costing you stars',
    navIcon: 'badge',
    isNew: true,
    badge: 'Staff Performance',
    headline: 'Find out which team members are earning 5 stars — and who\'s quietly losing them.',
    subheadline:
      "Give each staff member their own QR code. You'll know exactly who's delivering great service and who needs a conversation — before a pattern of bad reviews hits your Google page.",
    steps: [
      {
        icon: 'badge',
        title: 'Give each team member their own QR code',
        description:
          'Print a unique QR code on each person\'s name badge, uniform, or receipt footer. Takes a few minutes to set up and nothing else to change.',
      },
      {
        icon: 'person_search',
        title: 'Reviews link back to that specific person',
        description:
          'When a customer scans a staff member\'s QR and rates them, that feedback is saved to their profile — separate from everyone else\'s.',
      },
      {
        icon: 'leaderboard',
        title: 'See who\'s winning and who needs help',
        description:
          'Your dashboard shows a simple leaderboard: each team member\'s average rating, total reviews, and any complaints that were flagged.',
      },
    ],
    benefits: [
      {
        icon: 'emoji_events',
        title: 'Reward your best people',
        description:
          'See who\'s consistently earning 5 stars and recognize them. When staff can see the leaderboard, service gets better on its own.',
      },
      {
        icon: 'school',
        title: 'Have better coaching conversations',
        description:
          'Instead of guessing who had a rough week, you know exactly which team member got a low rating and when. Makes coaching fast and fair.',
      },
      {
        icon: 'shield',
        title: 'Catch problems before Google does',
        description:
          'One team member with consistently low ratings can quietly drag down your whole Google score. Spot the pattern early and fix it first.',
      },
      {
        icon: 'groups',
        title: 'Works across multiple locations',
        description:
          'Have more than one business? Each location and each employee has their own data — all visible in one dashboard.',
      },
    ],
    stats: [
      { value: '40%', label: 'average service improvement after 60 days' },
      { value: '1 QR', label: 'per team member — no hardware needed' },
      { value: 'Live', label: 'leaderboard visible to management' },
    ],
    faq: [
      {
        q: 'Will my staff know they\'re being rated?',
        a: "Yes — and that's actually a good thing. When team members know their QR is live, service usually improves on its own. Most managers share the leaderboard in weekly meetings as a positive motivator.",
      },
      {
        q: 'What if a customer scans the wrong person\'s QR by mistake?',
        a: "The feedback is still saved — it just won't be linked to the right employee. We suggest printing QR codes directly on name badges or apron tags so it's clear whose is whose.",
      },
      {
        q: 'Can I control what my staff can see?',
        a: "Yes. You decide what each person can access. Staff can see only their own summary if you allow it. Only managers see the full leaderboard and unfiltered feedback.",
      },
    ],
    ctaHeadline: 'See exactly who\'s earning your 5-star reputation.',
    ctaSubheadline: 'Set up team QR codes in minutes. Works alongside your existing setup.',
  },

  'analytics': {
    slug: 'analytics',
    navTitle: 'Analytics Dashboard',
    navDescription: 'Real-time reputation insights across all locations',
    navIcon: 'analytics',
    badge: 'Reputation Intelligence',
    headline: 'See how your reputation is doing — right now, in plain English.',
    subheadline:
      "No spreadsheets, no guessing. Your Visto dashboard shows every rating, every scan, and every complaint in one clear view — so you always know where you stand and what to fix.",
    steps: [
      {
        icon: 'qr_code_scanner',
        title: 'Every scan is recorded automatically',
        description:
          'The moment a customer scans your QR code, Visto logs it — the time, the rating, the location, and which QR code was involved. Nothing slips through.',
      },
      {
        icon: 'fork_right',
        title: 'See the full picture at a glance',
        description:
          'Your dashboard shows how many people scanned, how many left a review, how many went to Google, and how many complaints were quietly intercepted.',
      },
      {
        icon: 'insights',
        title: 'Spot problems before they get worse',
        description:
          'Is Saturday night getting lower ratings than usual? Is one location doing better than another? Visto highlights patterns you\'d never notice on your own.',
      },
    ],
    benefits: [
      {
        icon: 'monitoring',
        title: 'Track your rating week by week',
        description:
          'See your average score over time and watch it improve as Visto works. Before-and-after charts make it easy to see the difference.',
      },
      {
        icon: 'location_on',
        title: 'Compare your locations side by side',
        description:
          'Running more than one business? Each location gets its own score, plus a combined view. Instantly see who\'s performing and who needs attention.',
      },
      {
        icon: 'schedule',
        title: 'Know your worst times of day',
        description:
          'Your dashboard shows when ratings dip — late Friday nights? Sunday brunch? Now you can fix the staffing or process, not just react to complaints.',
      },
      {
        icon: 'download',
        title: 'Share reports with your team',
        description:
          'Export a simple PDF or spreadsheet to share with your team or business partner. Great for monthly check-ins.',
      },
    ],
    stats: [
      { value: 'Live', label: 'dashboard updates in real time' },
      { value: '90 days', label: 'of trend data on all plans' },
      { value: '1 view', label: 'for all your locations and QR codes' },
    ],
    faq: [
      {
        q: 'Can I give my manager access without sharing everything?',
        a: 'Yes. You control who sees what. Managers can see full reports, while staff members only see their own ratings.',
      },
      {
        q: 'How much history can I see?',
        a: 'All plans include 90 days of data. Enterprise plans keep everything from day one with no expiry.',
      },
      {
        q: 'Can I get an alert if my rating starts dropping?',
        a: "Yes. Set a minimum score and Visto will text or email you the moment your weekly average falls below it.",
      },
    ],
    ctaHeadline: 'See your reputation clearly for the first time.',
    ctaSubheadline: 'Start your free trial and have your dashboard ready in minutes.',
  },
};

/** Ordered list for the mega menu */
export const PRODUCT_FEATURES = Object.values(featurePages);

export const SOLUTIONS = [
  { slug: 'restaurants', icon: 'restaurant', title: 'Restaurants & Bars', description: 'Stop 1-star moments from going public during busy service', isPopular: true },
  { slug: 'hotels', icon: 'hotel', title: 'Hotels & Hospitality', description: 'Manage guest feedback across every room and shift', isPopular: false },
  { slug: 'hair-salons', icon: 'content_cut', title: 'Salons & Spas', description: 'Capture the 5-star moment while clients are still with you', isPopular: false },
  { slug: 'dentists', icon: 'medical_services', title: 'Medical & Dental', description: 'Collect patient feedback privately before it reaches Google', isPopular: false },
  { slug: 'auto-repair', icon: 'car_repair', title: 'Auto Repair', description: 'Turn completed service jobs into 5-star Google reviews', isPopular: false },
  { slug: 'law-firms', icon: 'gavel', title: 'Law Firms', description: 'Build credibility with verified client reviews that win trust', isPopular: false },
];
